/**
 * Shared, browser-safe SVG drawing primitives for the EasyNutriFact panel renderers.
 *
 * Both the Nutrition Facts (`svg-us-nf.ts`) and Supplement Facts (`svg-us-sf.ts`) renderers run in
 * the BROWSER (the admin live preview, injected as inline <svg>) AND in the Node export worker
 * (→ resvg → PNG), so this module imports ONLY `./font-family` (a zero-import string constant) and
 * nothing node-only. Identical input → byte-identical SVG (no Date, no Math.random).
 *
 * All sizes/positions are in POINTS (1pt = 1/72in). FDA panel width ≈ 2.7in = 194.4pt.
 */
import { FONT_FAMILY, FONT_STACK } from "./font-family";

export { FONT_FAMILY, FONT_STACK };

/**
 * XML-1.0-illegal C0 control characters (everything below U+0020 EXCEPT tab/LF/CR, which XML
 * permits). resvg's XML parser and the browser both throw on these if they reach the markup
 * ("a non-XML character '\0' found"), so we strip them from user text before escaping. FDA panel
 * text is plain text — none of these carry meaning — so dropping them is lossless in practice.
 */
// eslint-disable-next-line no-control-regex
const XML_ILLEGAL_C0 = /[\x00-\x08\x0B\x0C\x0E-\x1F]/g;

/** FDA panel width: ~2.7in. The raster uses width = round(2.7*300) = 810px. */
export const PANEL_WIDTH_PT = 194.4;
/** Inner content padding (the black border box is inset by the rule weight). */
export const PAD = 6;
/** Heavy outer border weight. */
export const BORDER_W = 1;
/** The thick 7pt bar under the serving header. */
export const BAR_7 = 7;
/** Section divider bar (3pt). */
export const BAR_3 = 3;
/** Hairline rule between rows. */
export const HAIRLINE = 0.25;

export const X_LEFT = PAD;
export const X_RIGHT = PANEL_WIDTH_PT - PAD;

/**
 * XML-escape a value for safe inclusion in SVG text/attributes (& < > " '). Also strips
 * XML-1.0-illegal C0 control chars first (see XML_ILLEGAL_C0) so pasted/malicious input can't
 * make the resvg/browser XML parser throw at parse time.
 */
export function esc(value: string): string {
  return value
    .replace(XML_ILLEGAL_C0, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// ---------------------------------------------------------------------------
// Text measurement + word-wrap (long free-text rows: ingredient lists, the mandatory FDA
// footnote, the SF "Other ingredients:" line). The renderers have no real font metrics in the
// browser bundle, so we estimate advance width by an average em factor for the Helvetica-metric
// bundled face — deliberately a touch generous so wrapping errs toward staying INSIDE the panel.
// ---------------------------------------------------------------------------

/** Average glyph advance as a fraction of the point size for Liberation Sans / Helvetica mixed text. */
const AVG_CHAR_EM = 0.52;

/** Hard cap on wrapped lines per free-text block, so a pathological paste can't explode the panel. */
export const MAX_WRAP_LINES = 400;

/** Approximate rendered width (points) of `s` at point `size` in the bundled sans face. */
export function approxTextWidth(s: string, size: number): number {
  return s.length * size * AVG_CHAR_EM;
}

/**
 * Greedy word-wrap `s` to lines that each fit within `maxWidth` points at `size`. Breaks on
 * whitespace; a single word longer than `maxWidth` is emitted un-split on its own line (never
 * dropped). Collapses runs of whitespace. Caps the result at MAX_WRAP_LINES (degrading
 * gracefully rather than growing the label without bound). Never returns an empty array.
 */
export function wrapText(s: string, size: number, maxWidth: number): string[] {
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];
  const lines: string[] = [];
  let line = words[0] as string;
  for (let i = 1; i < words.length; i++) {
    const word = words[i] as string;
    const candidate = `${line} ${word}`;
    if (approxTextWidth(candidate, size) <= maxWidth) {
      line = candidate;
    } else {
      lines.push(line);
      if (lines.length >= MAX_WRAP_LINES) return lines;
      line = word;
    }
  }
  lines.push(line);
  return lines;
}

/** Trim to a fixed precision and drop trailing zeros, so coordinates are byte-stable. */
export function num(n: number): string {
  return Number(n.toFixed(3)).toString();
}

export interface TextOpts {
  x: number;
  y: number;
  size: number;
  text: string;
  bold?: boolean;
  italic?: boolean;
  anchor?: "start" | "end" | "middle";
}

/** A single SVG <text> element at point `size`, in the bundled FDA font family. */
export function text({
  x,
  y,
  size,
  text: t,
  bold = false,
  italic = false,
  anchor = "start",
}: TextOpts): string {
  const weight = bold ? "bold" : "normal";
  const style = italic ? ` font-style="italic"` : "";
  return (
    `<text x="${num(x)}" y="${num(y)}" font-family="${esc(FONT_STACK)}" ` +
    `font-size="${num(size)}pt" font-weight="${weight}"${style} text-anchor="${anchor}" ` +
    `fill="#000">${esc(t)}</text>`
  );
}

/** A full-width horizontal rule at y with the given stroke-width (points). */
export function rule(y: number, strokeWidth: number): string {
  return (
    `<line x1="${num(X_LEFT)}" y1="${num(y)}" x2="${num(X_RIGHT)}" y2="${num(y)}" ` +
    `stroke="#000" stroke-width="${num(strokeWidth)}pt" />`
  );
}

/**
 * Wrap a rendered `body` (the drawn elements) in the SVG document: a white panel with the heavy
 * FDA border box at the given total `heightPt`. Identical for every US panel format.
 */
export function wrapPanelSvg(heightPt: number, body: string): string {
  const height = Number(heightPt.toFixed(3));
  const border =
    `<rect x="${num(BORDER_W / 2)}" y="${num(BORDER_W / 2)}" ` +
    `width="${num(PANEL_WIDTH_PT - BORDER_W)}" height="${num(height - BORDER_W)}" ` +
    `fill="#fff" stroke="#000" stroke-width="${num(BORDER_W)}" />`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" ` +
    `width="${num(PANEL_WIDTH_PT)}pt" height="${num(height)}pt" ` +
    `viewBox="0 0 ${num(PANEL_WIDTH_PT)} ${num(height)}">` +
    border +
    body +
    `</svg>`
  );
}
